import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { BrandProvider } from "./contexts/BrandContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import { PlayerProvider } from "./contexts/PlayerContext";
import { MiniPlayer } from "./components/layout/MiniPlayer";
import { InstallPrompt } from "./components/InstallPrompt";
import { Toaster } from "./components/ui/sonner";

// Pages
import LandingPage from "./pages/LandingPage";
import OperatorRegistration from "./pages/OperatorRegistration";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import TravelerAccessPage from "./pages/TravelerAccessPage";

// Admin Pages
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminCities from "./pages/admin/AdminCities";
import AdminAttractions from "./pages/admin/AdminAttractions";
import AdminAttractionAudio from "./pages/admin/AdminAttractionAudio";
import AdminOperators from "./pages/admin/AdminOperators";
import AdminCodes from "./pages/admin/AdminCodes";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminModeration from "./pages/admin/AdminModeration";
import AdminLanguageRequests from "./pages/admin/AdminLanguageRequests";
import AdminContentRequests from "./pages/admin/AdminContentRequests";
import AdminARAssets from "./pages/admin/AdminARAssets";
import AdminDeposits from "./pages/admin/AdminDeposits";
import AdminListings from "./pages/admin/AdminListings";
import AdminListingTickets from "./pages/admin/AdminListingTickets";
import AdminB2COrders from "./pages/admin/AdminB2COrders";
import AdminChat from "./pages/admin/AdminChat";
import AdminDiscounts from "./pages/admin/AdminDiscounts";
import AdminPageEditor, { AdminPagesList } from "./pages/admin/AdminPages";
import AdminBlog from "./pages/admin/AdminBlog";
import AdminAnalytics from "./pages/admin/AdminAnalytics";
import BlogPage from "./pages/BlogPage";
import BlogPostPage from "./pages/BlogPostPage";
import ChatWidget from "./components/ChatWidget";
import ExperiencePage from "./pages/ExperiencePage";
import CheckoutPage from "./pages/CheckoutPage";
import OrderSuccessPage from "./pages/OrderSuccessPage";
import ContactPage from "./pages/ContactPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import CityDetailsPage from "./pages/CityDetailsPage";

// Operator Pages
import OperatorDashboard from "./pages/operator/OperatorDashboard";
import OperatorCodes from "./pages/operator/OperatorCodes";
import OperatorBranding from "./pages/operator/OperatorBranding";
import OperatorCredits from "./pages/operator/OperatorCredits";
import OperatorAnalytics from "./pages/operator/OperatorAnalytics";
import OperatorTeam from "./pages/operator/OperatorTeam";
import OperatorLanguages from "./pages/operator/OperatorLanguages";
import OperatorContentRequests from "./pages/operator/OperatorContentRequests";

// Traveler Pages
import TravelerDashboard from "./pages/traveler/TravelerDashboard";
import TravelerGuide from "./pages/traveler/TravelerGuide";
import TravelerRedeem from "./pages/traveler/TravelerRedeem";

import "./App.css";

function RedeemRedirect() {
    const [searchParams] = useSearchParams();
    const code = searchParams.get('code') || '';
    return <Navigate to={`/access${code ? `?code=${code}` : ''}`} replace />;
}

function ProtectedRoute({ children, roles }) {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen bg-[#0A0D14] flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    if (roles && !roles.includes(user.role)) {
        if (user.role === 'admin') return <Navigate to="/admin" replace />;
        if (user.role === 'operator') return <Navigate to="/operator" replace />;
        return <Navigate to="/my-guides" replace />;
    }

    return children;
}

import { useAnalytics } from "./hooks/useAnalytics";

function AppRoutes() {
    const { user, loading } = useAuth();
    useAnalytics();

    if (loading) {
        return (
            <div className="min-h-screen bg-[#0A0D14] flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <Routes>
            {/* Public Routes */}
            <Route path="/" element={user ? (
                user.role === 'admin' ? <Navigate to="/admin" /> :
                user.role === 'operator' ? <Navigate to="/operator" /> :
                <Navigate to="/my-guides" />
            ) : <LandingPage />} />
            <Route path="/login" element={user ? <Navigate to="/" /> : <LoginPage />} />
            <Route path="/register" element={user ? <Navigate to="/" /> : <RegisterPage />} />
            <Route path="/register/operator" element={user ? <Navigate to="/" /> : <OperatorRegistration />} />
            <Route path="/access" element={user ? <Navigate to="/my-guides" /> : <TravelerAccessPage />} />
            <Route path="/redeem" element={<RedeemRedirect />} />
            <Route path="/experience/:listingId" element={<ExperiencePage />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="/order-success" element={<OrderSuccessPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="/city/:citySlug" element={<CityDetailsPage />} />

            {/* Admin Routes */}
            <Route path="/admin" element={<ProtectedRoute roles={['admin', 'content_manager']}><AdminDashboard /></ProtectedRoute>} />
            <Route path="/admin/cities" element={<ProtectedRoute roles={['admin', 'content_manager']}><AdminCities /></ProtectedRoute>} />
            <Route path="/admin/attractions" element={<ProtectedRoute roles={['admin', 'content_manager']}><AdminAttractions /></ProtectedRoute>} />
            <Route path="/admin/attractions/:attractionId/audio" element={<ProtectedRoute roles={['admin', 'content_manager']}><AdminAttractionAudio /></ProtectedRoute>} />
            <Route path="/admin/operators" element={<ProtectedRoute roles={['admin']}><AdminOperators /></ProtectedRoute>} />
            <Route path="/admin/codes" element={<ProtectedRoute roles={['admin']}><AdminCodes /></ProtectedRoute>} />
            <Route path="/admin/settings" element={<ProtectedRoute roles={['admin']}><AdminSettings /></ProtectedRoute>} />
            <Route path="/admin/moderation" element={<ProtectedRoute roles={['admin']}><AdminModeration /></ProtectedRoute>} />
            <Route path="/admin/language-requests" element={<ProtectedRoute roles={['admin']}><AdminLanguageRequests /></ProtectedRoute>} />
            <Route path="/admin/content-requests" element={<ProtectedRoute roles={['admin']}><AdminContentRequests /></ProtectedRoute>} />
            <Route path="/admin/attractions/:attractionId/ar" element={<ProtectedRoute roles={['admin']}><AdminARAssets /></ProtectedRoute>} />
            <Route path="/admin/deposits" element={<ProtectedRoute roles={['admin']}><AdminDeposits /></ProtectedRoute>} />
            <Route path="/admin/listings" element={<ProtectedRoute roles={['admin']}><AdminListings /></ProtectedRoute>} />
            <Route path="/admin/listings/:listingId/tickets" element={<ProtectedRoute roles={['admin']}><AdminListingTickets /></ProtectedRoute>} />
            <Route path="/admin/b2c-orders" element={<ProtectedRoute roles={['admin']}><AdminB2COrders /></ProtectedRoute>} />
            <Route path="/admin/chat" element={<ProtectedRoute roles={['admin']}><AdminChat /></ProtectedRoute>} />
            <Route path="/admin/discounts" element={<ProtectedRoute roles={['admin']}><AdminDiscounts /></ProtectedRoute>} />
            <Route path="/admin/pages" element={<ProtectedRoute roles={['admin']}><AdminPagesList /></ProtectedRoute>} />
            <Route path="/admin/pages/:slug" element={<ProtectedRoute roles={['admin']}><AdminPageEditor /></ProtectedRoute>} />
            <Route path="/admin/blog" element={<ProtectedRoute roles={['admin']}><AdminBlog /></ProtectedRoute>} />
            <Route path="/admin/analytics" element={<ProtectedRoute roles={['admin']}><AdminAnalytics /></ProtectedRoute>} />

            {/* Operator Routes */}
            <Route path="/operator" element={<ProtectedRoute roles={['admin', 'operator']}><OperatorDashboard /></ProtectedRoute>} />
            <Route path="/operator/codes" element={<ProtectedRoute roles={['admin', 'operator']}><OperatorCodes /></ProtectedRoute>} />
            <Route path="/operator/branding" element={<ProtectedRoute roles={['admin', 'operator']}><OperatorBranding /></ProtectedRoute>} />
            <Route path="/operator/credits" element={<ProtectedRoute roles={['admin', 'operator']}><OperatorCredits /></ProtectedRoute>} />
            <Route path="/operator/analytics" element={<ProtectedRoute roles={['admin', 'operator']}><OperatorAnalytics /></ProtectedRoute>} />
            <Route path="/operator/team" element={<ProtectedRoute roles={['operator']}><OperatorTeam /></ProtectedRoute>} />
            <Route path="/operator/languages" element={<ProtectedRoute roles={['admin', 'operator']}><OperatorLanguages /></ProtectedRoute>} />
            <Route path="/operator/content-requests" element={<ProtectedRoute roles={['admin', 'operator']}><OperatorContentRequests /></ProtectedRoute>} />

            {/* Traveler Routes */}
            <Route path="/my-guides" element={<ProtectedRoute roles={['traveler', 'admin', 'operator']}><TravelerDashboard /></ProtectedRoute>} />
            <Route path="/guide/:attractionId" element={<ProtectedRoute roles={['traveler', 'admin', 'operator']}><TravelerGuide /></ProtectedRoute>} />

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

function App() {
    return (
        <HelmetProvider>
        <AuthProvider>
            <BrandProvider>
                <LanguageProvider>
                    <BrowserRouter>
                        <PlayerProvider>
                            <AppRoutes />
                            <MiniPlayer />
                            <InstallPrompt />
                        </PlayerProvider>
                        <Toaster position="top-right" richColors />
                        <ChatWidget />
                    </BrowserRouter>
                </LanguageProvider>
            </BrandProvider>
        </AuthProvider>
        </HelmetProvider>
    );
}

export default App;
